import { z } from 'zod';
import { REGIMES, UNIT_NATURES } from '../lmd/structure';

export const CURRICULUM_STATUSES = ['draft', 'published'] as const;

export const CourseSchema = z.object({
  id: z.uuid(),
  unitId: z.uuid(),
  name: z.string(),
  nameAr: z.string().nullable(),
  credits: z.number().int(),
  coefficient: z.number(),
  regime: z.enum(REGIMES),
  hoursCm: z.number(),
  hoursTd: z.number(),
  hoursTp: z.number(),
  position: z.number().int(),
});
export type Course = z.infer<typeof CourseSchema>;

export const UnitSchema = z.object({
  id: z.uuid(),
  semesterId: z.uuid(),
  code: z.string(),
  name: z.string(),
  nameAr: z.string().nullable(),
  nature: z.enum(UNIT_NATURES),
  eliminatoryGrade: z.number().nullable(),
  position: z.number().int(),
  /** Derived: sum of ECUE credits (max for an optional UE). */
  credits: z.number().int(),
  /** Derived: sum of ECUE coefficients. */
  coefficient: z.number(),
  courses: z.array(CourseSchema),
});
export type Unit = z.infer<typeof UnitSchema>;

export const SemesterSchema = z.object({
  id: z.uuid(),
  index: z.number().int(),
  levelId: z.uuid().nullable(),
  targetCredits: z.number().int(),
  credits: z.number().int(),
  units: z.array(UnitSchema),
});
export type Semester = z.infer<typeof SemesterSchema>;

export const CurriculumSummarySchema = z.object({
  id: z.uuid(),
  programId: z.uuid(),
  programCode: z.string(),
  programName: z.string(),
  name: z.string(),
  status: z.enum(CURRICULUM_STATUSES),
  version: z.number().int(),
  totalCredits: z.number().int(),
  semesterCount: z.number().int(),
  habilitationFrom: z.number().int().nullable(),
  habilitationTo: z.number().int().nullable(),
  publishedAt: z.string().nullable(),
  /** Enrollments attached to this curriculum (a published curriculum in use cannot be deleted). */
  enrollments: z.number().int(),
});
export type CurriculumSummary = z.infer<typeof CurriculumSummarySchema>;

const Weights = z.object({ DS: z.number(), TP: z.number(), EXAM: z.number() });

export const CurriculumTreeSchema = CurriculumSummarySchema.extend({
  semesters: z.array(SemesterSchema),
  /** Tenant assessment weights per régime (percent), for the inspector hint. */
  assessmentWeights: z.object({ MIXTE: Weights, CC: Weights, EXAM: Weights }),
  validation: z.object({
    ok: z.boolean(),
    issues: z.array(
      z.object({
        semesterId: z.uuid(),
        index: z.number().int(),
        credits: z.number().int(),
        target: z.number().int(),
      }),
    ),
  }),
});
export type CurriculumTree = z.infer<typeof CurriculumTreeSchema>;

export const CurriculumQuerySchema = z.object({ programId: z.uuid().optional() });

export const CreateCurriculumSchema = z.object({
  programId: z.uuid(),
  name: z.string().trim().min(2).max(160),
  habilitationFrom: z.number().int().min(2000).max(2100).nullable().optional(),
  habilitationTo: z.number().int().min(2000).max(2100).nullable().optional(),
});
export type CreateCurriculum = z.infer<typeof CreateCurriculumSchema>;

export const UpdateCurriculumSchema = CreateCurriculumSchema.omit({ programId: true }).partial();

const code = z
  .string()
  .trim()
  .regex(/^[A-Z]{2,4}[0-9]{1,3}$/, 'curriculum.errors.unitCode');
const label = z.string().trim().min(2, 'curriculum.errors.nameRequired').max(160);

export const CreateUnitSchema = z.object({
  code,
  name: label,
  nameAr: z.string().trim().max(160).nullable().optional(),
  nature: z.enum(UNIT_NATURES),
});
export type CreateUnit = z.infer<typeof CreateUnitSchema>;

export const UpdateUnitSchema = CreateUnitSchema.partial().extend({
  eliminatoryGrade: z.number().min(0).max(20).nullable().optional(),
  position: z.number().int().min(0).optional(),
});
export type UpdateUnit = z.infer<typeof UpdateUnitSchema>;

export const CreateCourseSchema = z.object({
  name: label,
  nameAr: z.string().trim().max(160).nullable().optional(),
  credits: z.number().int().min(0).max(30),
  coefficient: z.number().min(0).max(20),
  regime: z.enum(REGIMES),
  hoursCm: z.number().min(0).max(200).default(0),
  hoursTd: z.number().min(0).max(200).default(0),
  hoursTp: z.number().min(0).max(200).default(0),
});
export type CreateCourse = z.input<typeof CreateCourseSchema>;

export const UpdateCourseSchema = CreateCourseSchema.partial().extend({
  position: z.number().int().min(0).optional(),
});
export type UpdateCourse = z.input<typeof UpdateCourseSchema>;
